import { useDeferredValue, useMemo, useState } from 'react';
import type { RepViewProspectRowData } from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';

interface FilteredRepViewProspects {
  deferredSearchText: string;
  filteredProspects: RepViewProspectRowData[];
  searchText: string;
  setSearchText: (text: string) => void;
}

/**
 * Owns the prospects list search state and returns the subset of `prospects`
 * matching the current query.
 *
 * Matching is case-insensitive against the prospect's name and address.
 * Whitespace-only queries are ignored. The query is deferred and trimmed before
 * it reaches the filter, so typing stays responsive while the full owned-prospect
 * list is re-filtered.
 *
 * @param prospects - The full list of prospects to filter
 * @returns `filteredProspects`, `searchText`, `deferredSearchText`
 * and `setSearchText`.
 */
export const useFilteredRepViewProspects = (
  prospects: RepViewProspectRowData[],
): FilteredRepViewProspects => {
  const [searchText, setSearchText] = useState('');
  // Keeps the empty state in sync with the rows; otherwise we sometimes get an empty state with empty text
  const deferredSearchText = useDeferredValue(searchText).trim();

  const filteredProspects = useMemo(() => {
    const query = deferredSearchText.toLowerCase();
    if (!query) {
      return prospects;
    }

    return prospects.filter(
      (prospect) =>
        prospect.name.toLowerCase().includes(query) ||
        prospect.address.toLowerCase().includes(query),
    );
  }, [deferredSearchText, prospects]);

  return { deferredSearchText, filteredProspects, searchText, setSearchText };
};
